/**
 * "Give a shout out": pick one or more people, say what they did, tag a
 * company value. Submitting creates a PENDING shout out; nothing is public
 * until a Shout Out Admin approves it, and the dialog says so.
 *
 * People come from the shared Vera directory (real names, composite oids that
 * match the BFF session). Pre-provisioned people with no oid yet are left out:
 * a shout out keyed to `pending:<email>` would never match them later.
 */

import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  Input,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useVeraDirectory } from '@entities/vera-directory';
import { useAuth } from '@shared/providers/AuthProvider';
import { borderRadius, semanticColors, veracityColors } from '@shared/ui';
import { useCreateShoutOut, type ShoutOutValue } from './api';
import { Initials } from './bits';

const MAX_CHARS = 500;
const MAX_TITLE_CHARS = 80;
const MAX_RECIPIENTS = 10;

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  values: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  value: {
    padding: '7px 14px',
    borderRadius: borderRadius.circular,
    border: `1px solid ${semanticColors.borderDefault}`,
    backgroundColor: '#FFFFFF',
    color: veracityColors.midnightCharcoal,
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '2px' },
  },
  valueOn: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: '#FFFFFF',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  count: { fontSize: '12px', color: semanticColors.textSecondary, textAlign: 'right' },
  sent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  error: { color: tokens.colorPaletteRedForeground1, fontSize: '13px' },
});

export interface ShoutOutComposerProps {
  open: boolean;
  onClose: () => void;
  values: ShoutOutValue[];
}

export function ShoutOutComposer({ open, onClose, values }: ShoutOutComposerProps) {
  const styles = useStyles();
  const { user } = useAuth();
  const directory = useVeraDirectory(open);
  const create = useCreateShoutOut();

  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [valueId, setValueId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const people = useMemo(
    () =>
      (directory.data ?? [])
        .filter((p) => !p.isPending && p.oid !== user?.oid)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [directory.data, user?.oid]
  );
  const byOid = useMemo(() => new Map(people.map((p) => [p.oid, p])), [people]);
  const matches = people
    .filter((p) => !selected.includes(p.oid))
    .filter((p) => !query.trim() || p.name.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 30);

  const reset = () => {
    setSelected([]);
    setQuery('');
    setTitle('');
    setMessage('');
    setValueId(null);
    setSent(false);
    create.reset();
  };

  const close = () => {
    onClose();
    // After the close animation, so the form does not visibly empty.
    setTimeout(reset, 200);
  };

  const canSend =
    selected.length > 0 &&
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    valueId !== null &&
    !create.isPending;

  const submit = () => {
    if (!canSend || !valueId) return;
    create.mutate(
      {
        recipients: selected.map((oid) => ({ oid, name: byOid.get(oid)?.name ?? oid })),
        title: title.trim(),
        message: message.trim(),
        valueId,
      },
      { onSuccess: () => setSent(true) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{sent ? 'Shout out sent' : 'Give a shout out'}</DialogTitle>
          <DialogContent>
            {sent ? (
              <div className={styles.sent}>
                <span>Sent to a moderator for approval.</span>
                <span style={{ color: semanticColors.textSecondary }}>
                  It shows up in Veraciter Central once a Shout Out Admin approves it.
                </span>
              </div>
            ) : (
              <div className={styles.form}>
                <Field
                  label="Who is it for?"
                  required
                  validationState={directory.isError ? 'error' : 'none'}
                  validationMessage={
                    directory.isError
                      ? 'Could not load the people list. Sign out and back in; if it keeps happening, ask a Vera admin to check your access.'
                      : undefined
                  }
                >
                  <TagPicker
                    selectedOptions={selected}
                    onOptionSelect={(_, d) => {
                      // The loading / no-match rows are options too; never select them.
                      if (d.value.startsWith('__')) return;
                      if (d.selectedOptions.length <= MAX_RECIPIENTS) setSelected(d.selectedOptions);
                      setQuery('');
                    }}
                  >
                    <TagPickerControl>
                      <TagPickerGroup aria-label="Selected people">
                        {selected.map((oid) => (
                          <Tag key={oid} shape="circular" value={oid}>
                            {byOid.get(oid)?.name ?? oid}
                          </Tag>
                        ))}
                      </TagPickerGroup>
                      <TagPickerInput
                        aria-label="Search people"
                        placeholder={selected.length === 0 ? 'Search by name' : ''}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </TagPickerControl>
                    <TagPickerList>
                      {directory.isLoading ? (
                        <TagPickerOption value="__loading">
                          Loading people…
                        </TagPickerOption>
                      ) : matches.length === 0 ? (
                        <TagPickerOption value="__none">
                          No one matches
                        </TagPickerOption>
                      ) : (
                        matches.map((p) => (
                          <TagPickerOption key={p.oid} value={p.oid} media={<Initials name={p.name} size={24} />}>
                            {p.name}
                          </TagPickerOption>
                        ))
                      )}
                    </TagPickerList>
                  </TagPicker>
                </Field>

                <Field label="Title" required hint="The headline on the card. The details show when someone opens it.">
                  <Input
                    value={title}
                    maxLength={MAX_TITLE_CHARS}
                    placeholder="Saved the renewal at 4:59pm"
                    onChange={(_, d) => setTitle(d.value)}
                  />
                </Field>

                <Field label="What did they do?" required>
                  <Textarea
                    value={message}
                    maxLength={MAX_CHARS}
                    rows={4}
                    resize="vertical"
                    placeholder="Be specific: what happened, and why it mattered."
                    onChange={(_, d) => setMessage(d.value)}
                  />
                  <span className={styles.count}>
                    {message.length}/{MAX_CHARS}
                  </span>
                </Field>

                <Field label="Which value does it show?" required>
                  <div className={styles.values} role="radiogroup" aria-label="Company value">
                    {values.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        role="radio"
                        aria-checked={valueId === v.id}
                        className={mergeClasses(styles.value, valueId === v.id && styles.valueOn)}
                        onClick={() => setValueId(v.id)}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </Field>

                {create.error && <span className={styles.error}>{create.error.message}</span>}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {sent ? (
              <>
                <Button appearance="secondary" onClick={reset}>
                  Give another
                </Button>
                <Button appearance="primary" onClick={close}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button appearance="secondary" onClick={close}>
                  Cancel
                </Button>
                <Button appearance="primary" disabled={!canSend} onClick={submit}>
                  {create.isPending ? 'Sending…' : 'Send for approval'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
